import "katex/dist/katex.min.css";
import { Children, isValidElement, memo, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import type { PluggableList } from "unified";
import { cn } from "~/lib/utils";
import { CodeBlock } from "./CodeBlock";
import { ArtifactRef } from "./artifacts/ArtifactRef";
import { remarkArtifactRefs } from "./remarkArtifactRefs";

interface MarkdownProps {
  text: string;
}

const remarkPlugins: PluggableList = [remarkGfm, remarkMath, remarkArtifactRefs];

// `errorColor: "inherit"` keeps math that fails to parse looking like the
// surrounding prose instead of KaTeX's default red, which matters because
// formulas arrive a token at a time and are briefly incomplete. `strict:
// false` suppresses warnings on the Unicode the model likes to mix in.
const rehypePlugins: PluggableList = [[rehypeKatex, { errorColor: "inherit", strict: false }]];

// Lightweight markdown renderer scoped to assistant message bubbles. Pulls
// in nothing from the v1 assistant pipeline; just `react-markdown` + GFM,
// KaTeX math, plus our `[[name]]` -> <ArtifactRef/> plugin.
export const Markdown = memo(function Markdown({ text }: MarkdownProps) {
  return (
    <div className="text-sm leading-6">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});

// Pull the raw source and `language-*` hint out of a fenced block's inner
// <code> element so it can be re-rendered through Prism.
function extractFencedCode(children: ReactNode): { code: string; language: string } {
  const [first] = Children.toArray(children);
  if (!isValidElement<{ className?: string; children?: ReactNode }>(first)) {
    return { code: "", language: "" };
  }
  const text = Children.toArray(first.props.children)
    .filter((child): child is string => typeof child === "string")
    .join("");
  const language = /language-(\w+)/.exec(first.props.className ?? "")?.[1] ?? "";
  return { code: text.replace(/\n$/, ""), language };
}

// `artifact-ref` is emitted by remarkArtifactRefs; react-markdown's
// `components` map keys aren't typed for custom elements, so we cast.
const components = {
  "artifact-ref": ({ name }: { name: string }) => <ArtifactRef name={name} />,
  p: ({ className, ...props }) => (
    <p className={cn("my-3 first:mt-0 last:mb-0", className)} {...props} />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn("font-medium text-primary underline underline-offset-4", className)}
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn("my-3 ml-5 list-disc first:mt-0 last:mb-0 [&>li]:mt-1", className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn("my-3 ml-5 list-decimal first:mt-0 last:mb-0 [&>li]:mt-1", className)}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn("my-3 border-l-2 border-border pl-3 italic first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn("my-4 border-border", className)} {...props} />
  ),
  h1: ({ className, ...props }) => (
    <h1 className={cn("mt-4 mb-2 text-base font-semibold first:mt-0", className)} {...props} />
  ),
  h2: ({ className, ...props }) => (
    <h2 className={cn("mt-4 mb-2 text-base font-semibold first:mt-0", className)} {...props} />
  ),
  h3: ({ className, ...props }) => (
    <h3 className={cn("mt-3 mb-1 text-sm font-semibold first:mt-0", className)} {...props} />
  ),
  // Inline code only. Fenced blocks are handled by `pre` below, which routes
  // through CodeBlock; className is preserved either way so `pre` can read the
  // `language-*` hint off the inner code element.
  code: ({ className, children, ...props }) => {
    const isBlock = typeof className === "string" && className.includes("language-");
    return (
      <code
        className={cn(
          !isBlock &&
            "rounded border border-border bg-background/60 px-1 py-0.5 font-mono text-[0.875em]",
          className
        )}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    const { code, language } = extractFencedCode(children);
    return <CodeBlock code={code} language={language} />;
  },
  table: ({ className, ...props }) => (
    <div className="my-3 overflow-x-auto first:mt-0 last:mb-0">
      <table
        className={cn("w-full border-collapse text-left text-[0.95em]", className)}
        {...props}
      />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th className={cn("border-b border-border px-2 py-1 font-semibold", className)} {...props} />
  ),
  td: ({ className, ...props }) => (
    <td className={cn("border-b border-border/60 px-2 py-1", className)} {...props} />
  ),
} as Parameters<typeof ReactMarkdown>[0]["components"];
